/**
 * The guided workout's speech. In Portuguese, once the workout's own voice is loaded (src/utils/voice: Piper's
 * "Faber", made in the browser), it speaks every line. Otherwise, and in English, the Web Speech API
 * (window.speechSynthesis) reads it with the device's own voice. Falls back silently if neither is available.
 */
import { isVoiceReady, playLine, prepareLine, setVoiceGain, stopVoice } from './voice';
import { speakablePt } from './speechText';

const usesOwnVoice = (lang) => lang === 'pt-BR' && isVoiceReady();

// The voice volume as a gain (utils/voiceVolume.js): the own voice can go above 1, the device voice tops out at 1
let volume = 1;
export const setSpeechVolume = (gain) => {
  volume = gain;
  setVoiceGain(gain);
};

// "uma série", "duas repetições", "haltéres": what both voices say wrong when they read the written line
const forTheEar = (text, lang) => (lang === 'pt-BR' ? speakablePt(text) : text);

export const speak = (written, lang = 'pt-BR') => {
  if (!written) return;
  const text = forTheEar(written, lang);

  if (usesOwnVoice(lang)) {
    window.speechSynthesis?.cancel();
    playLine(text).catch(() => {});
    return;
  }

  if (!window.speechSynthesis) return;

  // Cancel any ongoing speech before starting new one
  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang;
  utterance.rate = 1.05;
  utterance.pitch = 1.0;
  utterance.volume = Math.min(1, volume);

  window.speechSynthesis.speak(utterance);
};

// Makes a line before it's needed (the own voice takes a moment per line); a no-op with the device voice
export const prepareSpeech = (written, lang = 'pt-BR') => {
  if (!written || !usesOwnVoice(lang)) return;
  prepareLine(forTheEar(written, lang)).catch(() => {});
};

export const cancelSpeech = () => {
  stopVoice();
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
};
